import { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import FormSection from '../../../Common/Form/FormSection';
import HintCallout from '../../../Common/HintCallout';
import axiosInstance from '../../../../../services/AxiosInstance';
import Api from '../../../../../constants/Api';
import { errorAlert, errorObjectAlert, successToast, warnAlertYesOrNo } from '../../../alert/defaultAlert';
import { formatFileSize, openAuthenticatedPdf, validateContractFile } from '../../Contract/ContractUtils';

/**
 * Contrato (PDF) do plano. Um por plano: enviar de novo substitui e sobe a versão. O arquivo
 * é enviado na hora, por rota própria, e por isso só aparece com o plano já salvo (é preciso
 * ter o id). O cliente lê esse contrato no ambiente de agendamento e aceita ao contratar.
 */
const PlanContractSection = ({ entityData }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    // Estado do contrato guardado aqui, e não devolvido ao entityData do formulário: trocar o
    // entityData dispara o preenchimento dos campos e apagaria edições ainda não salvas.
    const [contract, setContract] = useState(null);
    const current = contract ?? entityData;
    const planId = entityData?.id;
    const base = `${Api.subscription.plan}/${planId}/contract`;

    if (!planId) {
        return (
            <FormSection title="Contrato do plano" description="O cliente lê e aceita ao contratar pelo site." icon="fa-solid fa-file-contract" compact>
                <HintCallout icon="fa-info-circle">Salve o plano primeiro. Depois de salvo, o contrato padrão é gerado com os dados dele, e você também pode anexar um PDF próprio.</HintCallout>
            </FormSection>
        );
    }

    const upload = async (file) => {
        const invalid = validateContractFile(file);
        if (invalid) {
            errorAlert(invalid);
            return;
        }
        const body = new FormData();
        body.append('file', file);
        try {
            setBusy(true);
            const { data } = await axiosInstance.post(base, body);
            setContract(data);
            successToast(`Contrato próprio anexado (versão ${data.contractVersion}).`);
        } catch (error) {
            errorObjectAlert(error);
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const remove = () => warnAlertYesOrNo(
        'Remover o contrato próprio? O plano volta a usar o contrato padrão, gerado com os dados dele. Quem já aceitou continua com a cópia do contrato que aceitou.',
        async () => {
            try {
                setBusy(true);
                const { data } = await axiosInstance.delete(base);
                setContract(data);
            } catch (error) {
                errorObjectAlert(error);
            } finally {
                setBusy(false);
            }
        });

    const view = async () => {
        try {
            await openAuthenticatedPdf(base);
        } catch (error) {
            errorAlert(error.message || 'Não foi possível abrir o contrato.');
        }
    };

    const hasContract = Boolean(current.hasContract);
    return (
        <FormSection title="Contrato do plano" description="O cliente lê e aceita ao contratar pelo site." icon="fa-solid fa-file-contract" compact>
            {hasContract ? (
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="me-auto">
                        <i className="fa-solid fa-file-pdf me-2"></i>
                        <strong>{current.contractFileName || 'contrato.pdf'}</strong>
                        <span className="text-muted ms-2">versão {current.contractVersion}</span>
                        {current.contractFileSize ? <span className="text-muted ms-2">{formatFileSize(current.contractFileSize)}</span> : null}
                    </span>
                    <Button size="sm" variant="outline-secondary" onClick={view} disabled={busy}>Ver</Button>
                    <Button size="sm" variant="outline-primary" onClick={() => inputRef.current?.click()} disabled={busy}>Substituir</Button>
                    <Button size="sm" variant="outline-danger" onClick={remove} disabled={busy}>Remover</Button>
                </div>
            ) : (
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="text-muted me-auto">
                        <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                        Contrato padrão, gerado com os dados da empresa e deste plano (preço, ciclo, benefícios e limites).
                    </span>
                    <Button size="sm" variant="outline-secondary" onClick={view} disabled={busy}>Ver contrato padrão</Button>
                    <Button size="sm" variant="outline-primary" onClick={() => inputRef.current?.click()} disabled={busy}>Usar contrato próprio (PDF)</Button>
                </div>
            )}
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="d-none"
                onChange={(event) => upload(event.target.files?.[0])} />
            <div className="mt-3">
                <HintCallout icon="fa-info-circle">
                    Sem contrato próprio, o sistema gera o contrato padrão e o atualiza sozinho quando o preço, os benefícios ou os limites do plano mudam. Se você anexar um PDF próprio, ele vale no lugar do gerado. Em qualquer caso, o cliente aceita a versão que está vendo, e quem já contratou mantém a cópia do contrato que aceitou.
                </HintCallout>
            </div>
        </FormSection>
    );
};

export default PlanContractSection;
